import React, { useState, useEffect } from 'react';
import api from '../api/client';
import Pagination from './TablePagination';

const DynamicInventoryTable = ({
    title,
    baseColumns,
    inventoryEndpoint,
    planningEndpoint,
    dateField = 'plan_date',
    qtyField = 'qty_boxes',
    keyField = 'sku',
    showActions = true
}) => {
    const [baseData, setBaseData] = useState([]);
    const [planningData, setPlanningData] = useState([]);
    const [dates, setDates] = useState([]);
    const [hiddenDates, setHiddenDates] = useState(() => {
        const saved = localStorage.getItem('hiddenDates');
        return saved ? JSON.parse(saved) : [];
      });
    const [showPastDates, setShowPastDates] = useState(false);
    const [showHiddenDatesModal, setShowHiddenDatesModal] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(1000);
    const [totalItems, setTotalItems] = useState(0);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'none' });
    const [editingCell, setEditingCell] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [saving, setSaving] = useState(false);
    const [notification, setNotification] = useState(null);

    useEffect(() => {
        localStorage.setItem('hiddenDates', JSON.stringify(hiddenDates));
      }, [hiddenDates]);

      
    const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
    };

    const today = new Date().setHours(0, 0, 0, 0);
    const visibleDates = dates.filter(date => 
        !hiddenDates.includes(date) && 
        (showPastDates || new Date(date) >= today)
    );

   // Обработчик начала редактирования
   const startEditing = (sku, date, value) => {
    if (saving) return; // Блокировка редактирования во время сохранения
    setEditingCell({ sku, date });
    setEditValue(value.toString());
  };
  
  // Сохранение изменений в API
  const saveEditing = async () => {
    if (!editingCell) return;
  
    try {
      setSaving(true);
      console.log('Saving data:', {
        sku: editingCell.sku,
        date: editingCell.date,
        value: editValue
      });
  
      // Форматируем дату для сервера
      const formattedDate = new Date(editingCell.date)
        .toISOString()
        .split('T')[0];
  
      const response = await api.put(`/api/shipping_plan`, {
        sku: editingCell.sku,
        plan_date: formattedDate,
        qty_boxes: parseFloat(editValue)
      });
  
      console.log('Server response:', response.data);
      
      // Принудительно обновляем данные
      await Promise.all([
        fetchPlanningData(),
        fetchBaseData()
      ]);
      
      showNotification('Changes saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving data:', error.response?.data || error.message);
      showNotification('Error saving changes!', 'error');
    } finally {
      setSaving(false);
      setEditingCell(null);
    }
  };

    const fetchBaseData = async () => {
        try {
            const response = await api.get(inventoryEndpoint, {
                params: {
                    page,
                    limit,
                    sort: sortConfig.key,
                    order: sortConfig.direction === 'none' ? 'asc' : sortConfig.direction
                }
            });
            
            const data = response.data.data || [];
            setBaseData(data);
            setTotalItems(response.data.total || 0);
        } catch (error) {
            console.error('Error fetching inventory data:', error);
        }
    };

    const fetchPlanningData = async () => {
        try {
            const response = await api.get(planningEndpoint);
            const planning = response.data.data || [];
            
            const uniqueDates = [...new Set(planning.map(item => item[dateField]))];
            const sortedDates = uniqueDates.sort((a, b) => new Date(a) - new Date(b));
            setDates(sortedDates);
            setPlanningData(planning);
        } catch (error) {
            console.error('Error fetching planning data:', error);
        }
    };

    const mergedData = baseData.map(item => {
        const plans = planningData.filter(p => p[keyField] === item[keyField]);
        const dateValues = {};
        
        const sortedPlans = plans.sort((a, b) => new Date(a[dateField]) - new Date(b[dateField]));
        let cumulativePlanned = 0;

        visibleDates.forEach(date => {
            const plan = sortedPlans.find(p => p[dateField] === date);
            const planned = plan ? plan[qtyField] : 0;
            cumulativePlanned += planned;
            
            dateValues[date] = {
                planned: planned,
                cumulativeMissing: (item.box_count || 0) - cumulativePlanned
            };
        });
        
        return { ...item, ...dateValues };
    });

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key ? 
                prev.direction === 'asc' ? 'desc' : 'asc' : 'asc'
        }));
    };

    const handleHideDate = (date) => {
        setHiddenDates(prev => {
          const updated = [...prev, date];
          return updated;
        });
      };

      const handleUnhideDate = (date) => {
        setHiddenDates(prev => prev.filter(d => d !== date));
      };

    useEffect(() => {
        fetchBaseData();
        if(planningEndpoint) fetchPlanningData();
    }, [page, limit, sortConfig]);

    
    return (
        <div className="inventory-table">
            <h2>{title}</h2>
            {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}
      
      {saving && <div className="saving-indicator">Saving...</div>}
            
            <div className="table-wrapper">
                <table className="data-table">
                    <thead>
                        <tr>
                            {baseColumns.map(col => (
                                <th 
                                    key={col.key} 
                                    rowSpan="2" 
                                    onClick={() => col.sortable && handleSort(col.key)}
                                >
                                    {col.label}
                                    {sortConfig.key === col.key && (
                                        <span className="sort-arrow">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                    )}
                                </th>
                            ))}
                            
                            {visibleDates.map(date => (
                                <th key={date} colSpan="2">
                                    {new Date(date).toLocaleDateString()}
                                    <button 
                                        onClick={() => handleHideDate(date)}
                                        className="exclude-date-btn"
                                    >
                                        Hide
                                    </button>
                                </th>
                            ))}
                            
                            {showActions && <th rowSpan="2">Actions</th>}
                        </tr>
                        <tr>
                            {visibleDates.map(date => (
                                <React.Fragment key={date}>
                                    <th>Planned</th>
                                    <th>Missing</th>
                                </React.Fragment>
                            ))}
                        </tr>
                    </thead>
                    
                    <tbody>
                        {mergedData.map((row, index) => (
                            <tr key={index}>
                                {baseColumns.map(col => (
                                    <td key={col.key}>
                                        {col.type === 'number' 
                                            ? Number(row[col.key]).toFixed(2)
                                            : row[col.key]}
                                    </td>
                                ))}
                                
                                {visibleDates.map(date => (
                                    <React.Fragment key={date}>
                                        <td className="planned" onDoubleClick={() => startEditing(row[keyField], date, row[date]?.planned)}>
                                        {editingCell?.sku === row[keyField] && editingCell?.date === date ? (
                                            <input
                                                className="editing-input"
                                                type="number"
                                                value={editValue}
                                                onChange={(e) => {
                                                const value = Math.max(0, parseFloat(e.target.value) || 0);
                                                setEditValue(value);
                                                }}
                                                onBlur={saveEditing}
                                                onKeyPress={(e) => e.key === 'Enter' && saveEditing()}
                                                autoFocus
                                                min="0"
                                                step="1"
                                                disabled={saving}
                                            />
                                            ) : (
                                            row[date]?.planned.toFixed(0)
                                        )}
                                        </td>
                                        <td className={`missing ${row[date]?.cumulativeMissing < 0 ? 'negative' : ''}`}>
                                        {row[date]?.cumulativeMissing.toFixed(0)}
                                        </td>
                                    </React.Fragment>
                                    ))}
                                
                                {showActions && (
                                    <td className="actions">
                                        <button className="edit-btn">Edit</button>
                                        <button className="delete-btn">Delete</button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="table-controls">
                <button 
                    onClick={() => setShowPastDates(!showPastDates)}
                    className="toggle-past-dates"
                >
                    {showPastDates ? 'Hide past dates' : 'Show past dates'}
                </button>
                <button 
                    onClick={() => setShowHiddenDatesModal(true)}
                    className="manage-hidden-dates"
                >
                    Managing hidden dates
                </button>
            </div>
            </div>
            
            <Pagination
                currentPage={page}
                totalPages={Math.ceil(totalItems / limit)}
                onPageChange={setPage}
                onItemsPerPageChange={setLimit}
            />

            {showHiddenDatesModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3 className="hidden-dates-header">Hidden dates</h3>
                        <ul className="hidden-dates-list">
                            {hiddenDates.map(date => (
                                <li key={date}>
                                    {new Date(date).toLocaleDateString()}
                                    <button 
                                        onClick={() => handleUnhideDate(date)}
                                        className="unhide-date-btn"
                                    >
                                        Show
                                    </button>
                                </li>
                            ))}
                        </ul>
                        <button 
                            onClick={() => setShowHiddenDatesModal(false)}
                            className="close-modal-btn"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DynamicInventoryTable;